import { cn } from "@/lib/utils";
import type { ReactNode } from "react";

interface DashboardMapViewLayoutProps {
  leadingContent?: ReactNode;
  facets: ReactNode;
  filterBadges?: ReactNode;
  children: ReactNode;
  className?: string;
}

/**
 * Scrolls like the list view. Page chrome (title, tiles, toolbar) sits above
 * a map pane that is as tall as this scroller, so scrolling moves the chrome
 * off screen and the map fills the viewport. The facet rail stays pinned to
 * the visible pane. Maps use cooperative gestures, so an unmodified wheel
 * over the map scrolls this pane instead of zooming.
 */
export function DashboardMapViewLayout({
  leadingContent,
  facets,
  filterBadges,
  children,
  className,
}: DashboardMapViewLayoutProps) {
  return (
    <div
      data-slot="dashboard-map-view-scroll"
      style={{ containerType: "size" }}
      className={cn(
        "flex min-h-0 min-w-0 flex-1 flex-col overflow-y-auto overscroll-contain",
        className,
      )}
    >
      <div className="flex w-full shrink-0 items-start gap-4">
        {facets ? (
          <div
            data-slot="dashboard-map-view-facets"
            className="sticky top-0 flex h-[100cqh] max-h-[100cqh] shrink-0 self-start"
          >
            {facets}
          </div>
        ) : null}
        <div className="flex min-w-0 flex-1 flex-col">
          {leadingContent ? (
            <div className="shrink-0 pb-4">{leadingContent}</div>
          ) : null}
          <div
            data-slot="dashboard-map-view-pane"
            className="flex h-[100cqh] min-h-[24rem] w-full shrink-0 flex-col"
          >
            {filterBadges ? (
              <div className="shrink-0">{filterBadges}</div>
            ) : null}
            <div className="min-h-0 flex-1">{children}</div>
          </div>
        </div>
      </div>
    </div>
  );
}
